import axios from "axios";
import {
  API_CONFIG,
  ENV_CONFIG,
  getBaseURL,
  getApiConfig,
} from "../../constants/endpoints";
import logger from "../../utils/secureLogger";

/**
 * Cliente HTTP unificado con interceptores, manejo de errores y fallback
 */
const { baseURL, apiKey } = getApiConfig();

const httpClient = axios.create({
  baseURL,
  timeout: API_CONFIG.TIMEOUT,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": apiKey,
  },
});

// Interceptor de request
httpClient.interceptors.request.use(
  (config) => {
    const apiKey = import.meta.env.VITE_API_KEY;
    config.headers["x-api-key"] = apiKey;

    return config;
  },
  (error) => {
    if (ENV_CONFIG.IS_DEVELOPMENT) {
      logger.error("Request configuration error", { message: error.message });
    }
    return Promise.reject(error);
  }
);

// Interceptor de response
httpClient.interceptors.response.use(
  (response) => {
    if (ENV_CONFIG.IS_DEVELOPMENT) {
      logger.debug("HTTP Response received", {
        status: response.status,
        url: response.config.url,
        dataSize: response.data ? Object.keys(response.data).length : 0,
      });
    }

    return response;
  },
  (error) => {
    if (ENV_CONFIG.IS_DEVELOPMENT) {
      logger.error("HTTP Request failed", {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url,
      });
    }

    return Promise.reject(error);
  }
);

// Cliente directo para fallback
export const directAPI = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": apiKey,
  },
});

// Agregar interceptor al directAPI para asegurar API key
directAPI.interceptors.request.use(
  (config) => {
    const apiKey = import.meta.env.VITE_API_KEY;
    config.headers["x-api-key"] = apiKey;
    return config;
  }
);



// Métodos con fallback automático
export const createRequestWithFallback = (endpoint, method = "get") => {
  return async (...args) => {
    try {
      if (method.toLowerCase() === "get") {
        return await httpClient.get(endpoint);
      } else if (method.toLowerCase() === "post") {
        const payload = args[0] || {};
        return await httpClient.post(endpoint, payload);
      }
    } catch (error) {
      if (method.toLowerCase() === "get") {
        return await directAPI.get(endpoint);
      } else if (method.toLowerCase() === "post") {
        const payload = args[0] || {};
        return await directAPI.post(endpoint, payload);
      }
      throw error;
    }
  };
};

// Clase de error personalizada
export class ApiError extends Error {
  constructor(message, status, details) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.details = details;
  }
}

export default httpClient;
